import { useTranslation } from '@thoughtspot/i18n';
import { getGuidsToSkip, isOrgUiEnabled } from '@thoughtspot/session-service';
import { useCallback, useMemo, useState } from 'react';
import { PrincipalVisibilityType } from "../../../../../services/generated/graphql-types";
import { useCreateUserMutation, useGetUserAccessibleOrgsQuery, useGetUserAndGroupsQuery, useSendWelcomeEmailMutation, } from "../../../../../services/user-group/user-group-service";
import { getTranslatedInfoForMessageCode } from "../../../../../utils/error-handling/error-strings-mapping";
import { getErrorObjectFromGraphQLErrors } from "../../../../../utils/error-handling/error-utils";
import { hasCreateEditInputError, } from '../../../../user-management/users/util';
import { captureFailureEvent, CHECKBOX_SECTION_PAGINATION_BATCHSIZE, getErrorCode, } from '../../../../user-management/util';
const INITIAL_FORM = {
    email: '',
    name: '',
    displayName: '',
    password: '',
    confirmPassword: '',
    allowOthersToShare: true,
    sendWelcomeEmail: true,
    welcomeEmailMessage: '',
};
const INITIAL_ERRORS = {
    isNameRequired: false,
    isDisplayNameRequired: false,
    isNameLong: false,
    isDisplayNameLong: false,
    isNotWhitelistedDomainEmail: false,
    isEmailEmpty: false,
    arePasswordsNotMatching: false,
    isPasswordEmpty: false,
    isConfirmPasswordEmpty: false,
    isPasswordValidationRuleFailed: false,
    isNameEmailNotSame: false,
};
const VALIDATE_ALL_FIELDS = {
    isNameUntouched: false,
    isDisplayNameUntouched: false,
    isPasswordUntouched: false,
    isConfirmPasswordUntouched: false,
    isEmailUntouched: false,
};
const FIELD_ERROR_MAP = {
    email: ['isEmailEmpty', 'isNotWhitelistedDomainEmail'],
    name: ['isNameRequired', 'isNameLong', 'isNameEmailNotSame'],
    displayName: ['isDisplayNameRequired', 'isDisplayNameLong'],
    password: [
        'isPasswordEmpty',
        'isPasswordValidationRuleFailed',
        'arePasswordsNotMatching',
    ],
    confirmPassword: ['isConfirmPasswordEmpty', 'arePasswordsNotMatching'],
};
export const useCreateUserForm = (currentOrgId, onSuccess) => {
    const { t } = useTranslation();
    const [form, setForm] = useState({ ...INITIAL_FORM });
    const [errors, setErrors] = useState({ ...INITIAL_ERRORS });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [groupSearchText, setGroupSearchText] = useState('');
    const { data: userAndGroupsData, loading: groupsLoading, } = useGetUserAndGroupsQuery(groupSearchText, getGuidsToSkip(), CHECKBOX_SECTION_PAGINATION_BATCHSIZE);
    const { data: accessibleOrgsData, loading: orgsLoading, } = useGetUserAccessibleOrgsQuery();
    const { createUser } = useCreateUserMutation();
    const { sendWelcomeEmail: sendWelcomeEmailFn, } = useSendWelcomeEmailMutation();
    const groupsList = userAndGroupsData?.getUserGroupsPrincipalList?.objects || [];
    const isClusterAdmin = isOrgUiEnabled() && currentOrgId === -1;
    const orgsList = isClusterAdmin
        ? accessibleOrgsData?.Data?.orgs || []
        : [];
    const updateField = useCallback((key, value) => {
        const updatedForm = { ...form, [key]: value };
        setForm(updatedForm);
        const errorKeysForField = FIELD_ERROR_MAP[key];
        if (errorKeysForField) {
            const revalidated = hasCreateEditInputError({ ...INITIAL_ERRORS }, {
                name: updatedForm.name.trim(),
                displayName: updatedForm.displayName.trim(),
                email: updatedForm.email.trim(),
                password: updatedForm.password,
                confirmPassword: updatedForm.confirmPassword,
                allowOthersToShare: updatedForm.allowOthersToShare,
            }, false, VALIDATE_ALL_FIELDS);
            const fieldErrors = {};
            errorKeysForField.forEach(k => {
                fieldErrors[k] = revalidated[k];
            });
            setErrors(prev => ({ ...prev, ...fieldErrors }));
        }
        setSubmitError(null);
    }, [form]);
    const actions = useMemo(() => ({
        setEmail: (value) => updateField('email', value),
        setName: (value) => updateField('name', value),
        setDisplayName: (value) => updateField('displayName', value),
        setPassword: (value) => updateField('password', value),
        setConfirmPassword: (value) => updateField('confirmPassword', value),
        setAllowOthersToShare: (value) => updateField('allowOthersToShare', value),
        setSendWelcomeEmail: (value) => updateField('sendWelcomeEmail', value),
        setWelcomeEmailMessage: (value) => updateField('welcomeEmailMessage', value),
    }), [updateField]);
    const validateStep1 = useCallback(() => {
        const validationResult = hasCreateEditInputError({ ...INITIAL_ERRORS }, {
            name: form.name.trim(),
            displayName: form.displayName.trim(),
            email: form.email.trim(),
            password: form.password,
            confirmPassword: form.confirmPassword,
            allowOthersToShare: form.allowOthersToShare,
        }, false, VALIDATE_ALL_FIELDS);
        setErrors(validationResult);
        return !Object.values(validationResult).some(Boolean);
    }, [form]);
    const handleSubmit = useCallback(async (selectedGroupIds, selectedOrgIds) => {
        setIsSubmitting(true);
        setSubmitError(null);
        try {
            const payload = {
                name: form.name.trim(),
                displayname: form.displayName.trim(),
                visibility: form.allowOthersToShare
                    ? PrincipalVisibilityType.Default
                    : PrincipalVisibilityType.NonSharable,
                groups: [...selectedGroupIds],
                properties: {
                    mail: form.email.trim(),
                    frontendURL: window.location.hostname,
                },
                isAllOrgContext: currentOrgId === -1,
                ...(form.password && { password: form.password }),
                ...(isOrgUiEnabled() &&
                    currentOrgId === -1 && {
                    orgids: [...selectedOrgIds],
                }),
            };
            const result = await createUser(payload);
            const createdUserId = result.data?.Session__createUser?.id;
            if (form.sendWelcomeEmail && createdUserId) {
                await sendWelcomeEmailFn(createdUserId, form.welcomeEmailMessage, false);
            }
            onSuccess();
            return true;
        }
        catch (err) {
            const graphQlError = getErrorObjectFromGraphQLErrors(err);
            const errorCode = getErrorCode(graphQlError);
            const errorText = t(getTranslatedInfoForMessageCode(errorCode).summary);
            setSubmitError(errorText);
            captureFailureEvent('CREATE_USER', graphQlError, errorText);
            return false;
        }
        finally {
            setIsSubmitting(false);
        }
    }, [form, currentOrgId, createUser, sendWelcomeEmailFn, onSuccess, t]);
    const resetForm = useCallback(() => {
        setForm({ ...INITIAL_FORM });
        setErrors({ ...INITIAL_ERRORS });
        setIsSubmitting(false);
        setSubmitError(null);
        setGroupSearchText('');
    }, []);
    return {
        form,
        actions,
        errors,
        isSubmitting,
        submitError,
        validateStep1,
        handleSubmit,
        resetForm,
        groupsData: {
            list: groupsList,
            loading: groupsLoading,
            searchText: groupSearchText,
            setSearchText: setGroupSearchText,
        },
        orgsData: {
            list: orgsList,
            loading: orgsLoading,
        },
    };
};
//# sourceMappingURL=useCreateUserForm.js.map